import React, {useEffect, useState} from 'react'
import {Card, Descriptions, Divider, message, Tabs} from '@convertlab/cl-design'
import Code from '../Code'
import DataTable from './Table'
import Note from "../Note";
import {getApiOrigin} from "../../../lib/fetch-api-server-url";

const pathPrefix = process.env.DOC_PREFIX || ''
const placeholderPathPrefix = process.env.PLACEHOLDER_DOC_PREFIX

const appOrigin = 'https://api.convertlab.com'

const DataRender = (props) => {
  const { path } = props
  const [data, setData] = useState(null)
  const [apiOrigin, setApiOrigin] = useState('')

  const setInnerHTML = (content) => {
    return { __html:
          (content ? replaceApi(content.replaceAll(`<a href='${placeholderPathPrefix}`, `<a href='${pathPrefix}`)) : content) }
  }

  const getColumn = (type = true, required = true) =>
    [
      {
        title: '参数',
        dataIndex: 'name',
        width: 250,
      },
      type && {
        title: '类型',
        dataIndex: 'type',
        width: 120,
      },
      required && {
        title: '必填',
        dataIndex: 'required',
        width: 100,
        render: (value) => <span>{value ? '是' : '否'}</span>,
      },
      {
        title: '描述',
        dataIndex: 'description',
        render: (value) => <span dangerouslySetInnerHTML={setInnerHTML(value)} />,
      },
    ].filter(Boolean)

  const replaceApi = (content) => {
    if (!apiOrigin || apiOrigin === appOrigin) return content
    else return content.replaceAll(appOrigin, apiOrigin)
  }

  useEffect(() => {
    fetch(`${pathPrefix}${path}`)
      .then((res) => res.json())
      .then((res) => setData(res))
      .catch((error) =>
        message.error(`获取 ${pathPrefix}${path} 路径下的数据失败：${error.message}`, 5000)
      )

    getApiOrigin().then(apiOrigin => {
      setApiOrigin(apiOrigin)
    })
  }, [])

  if (!data) {
    return null
  }

  const {
    summary,
    permission,
    sinceVersion,
    calllimit,
    method = '',
    async,
    url = '',
    allParams,
    codeSamples,
  } = data
  const examples = data.examples || []

  return [
    summary && [
      <span key="description" dangerouslySetInnerHTML={setInnerHTML(summary)} />,
      <Divider key="divider" />,
    ],
    <Descriptions column={1} colon={false} key="descriptions">
      {permission?.name && (
        <Descriptions.Item label="权限说明">
          需要获取 <a href={`${pathPrefix}/restapi/permissionscopes`}>{permission.name}</a>{' '}
          权限
        </Descriptions.Item>
      )}
      {sinceVersion && (
        <Descriptions.Item label="起始版本">{sinceVersion}</Descriptions.Item>
      )}
      {calllimit && calllimit !== '通用' && (
        <Descriptions.Item label="调用频次">{calllimit}</Descriptions.Item>
      )}
      {url && (<Descriptions.Item label="请求方式">
        {method} {replaceApi(url)}
      </Descriptions.Item>
      )}
      {async && (<Descriptions.Item label="接口类型">
          异步接口，调用后需要几秒钟到几分钟异步生效
        </Descriptions.Item>
      )}
      {examples.map((example, index) => [
        <Descriptions.Item
          key="example.summary"
          label={`请求示例${examples.length > 1 ? index + 1 : ''}`}
          className="ant-descriptions-item-block ant-descriptions-item-mark"
        >
          {example.summary && (
            <span dangerouslySetInnerHTML={setInnerHTML(example.summary)} />
          )}
        </Descriptions.Item>,
        <Descriptions.Item
          key="example.params"
          label="请求地址"
          className="ant-descriptions-item-block"
        >
          <Code
            language="text"
            code={
              example.params ? `${replaceApi(url)}?${example.params}` : `${method} ${replaceApi(url)}`
            }
          />
          {example?.description?.params?.length && (
            <DataTable
              columns={getColumn(false)}
              dataSource={example.description.params}
            />
          )}
        </Descriptions.Item>,
        example.requestBody && (
          <Descriptions.Item
            key="example.requestBody"
            label="请求体参数"
            className="ant-descriptions-item-block"
          >
            <Code language="json" code={example.requestBody} />
            {example?.description?.requestBody?.summary && (
              <span
                dangerouslySetInnerHTML={setInnerHTML(example.description.requestBody.summary)}
              />
            )}
            {(example?.description?.requestBody?.refs?.length ||
              example?.description?.requestBody?.fields?.length) && (
              <DataTable
                key="requestBody.fields"
                columns={getColumn()}
                refs={example.description.requestBody.refs}
                fields={example.description.requestBody.fields}
              />
            )}
          </Descriptions.Item>
        ),
        example.responseBody && (
          <Descriptions.Item
            key="example.responseBody"
            label={`返回示例${examples.length > 1 ? index + 1 : ''}`}
            className="ant-descriptions-item-block"
          >
            <Code language="json" code={example.responseBody} />
            {example?.description?.responseBody?.summary && (
              <span
                dangerouslySetInnerHTML={setInnerHTML(example.description.responseBody.summary)}
              />
            )}
            {(example?.description?.responseBody?.refs?.length ||
              example?.description?.responseBody?.fields?.length) && (
              <DataTable
                columns={getColumn(true, false)}
                refs={example.description.responseBody.refs}
                fields={example.description.responseBody.fields}
              />
            )}
          </Descriptions.Item>
        ),
        example.errorResponse && (
          <Descriptions.Item
            key="example.errorResponse"
            label={`失败示例${examples.length > 1 ? index + 1 : ''}`}
            className="ant-descriptions-item-block"
          >
            <Code language="json" code={example.errorResponse} />
          </Descriptions.Item>
        ),
        example?.failureCodes?.length && (
          <Descriptions.Item
            key="example.failureCodes"
            label="错误码列表"
            className="ant-descriptions-item-block ant-descriptions-item-mark"
          >
            <DataTable
              rowKey="code"
              columns={[
                {
                  title: '错误码',
                  dataIndex: 'code',
                },
                {
                  title: '描述',
                  dataIndex: 'description',
                },
              ]}
              fields={example.failureCodes}
            />
          </Descriptions.Item>
        ),
      ])}
      {allParams && [
        <Descriptions.Item
          key="allParams.summary"
          label="接口参数统一说明"
          className="ant-descriptions-item-block ant-descriptions-item-mark"
        >
          {allParams.summary && (
            <p dangerouslySetInnerHTML={setInnerHTML(allParams.summary)} />
          )}
        </Descriptions.Item>,
        allParams?.params?.length && (
          <Descriptions.Item
            key="allParams.params"
            label="请求参数说明"
            className="ant-descriptions-item-block"
          >
            <DataTable columns={getColumn(false)} fields={allParams.params} />
          </Descriptions.Item>
        ),
        (allParams?.refs?.length || allParams?.fields?.length ) && (
          <Descriptions.Item
            key="allParams.refs"
            label="请求/返回体参数说明"
            className="ant-descriptions-item-block"
          >
            <DataTable
              columns={getColumn()}
              refs={allParams.refs}
              fields={allParams.fields}
            />
          </Descriptions.Item>
        ),
        allParams?.failureCodes?.length && (
          <Descriptions.Item
            key="allParams.failureCodes"
            label="错误码列表"
            className="ant-descriptions-item-block ant-descriptions-item-mark"
          >
            <DataTable
              rowKey="code"
              columns={[
                {
                  title: '错误码',
                  dataIndex: 'code',
                },
                {
                  title: '描述',
                  dataIndex: 'description',
                },
              ]}
              fields={allParams.failureCodes}
            />
          </Descriptions.Item>
        ),
      ]}
      {codeSamples && (
        <Descriptions.Item
          label="代码示例"
          className="ant-descriptions-item-block ant-descriptions-item-mark"
        >
          <Card>
            <Tabs type="card">
              {Object.entries(codeSamples).map(([language, code]) => (
                <Tabs.TabPane tab={language} key={language}>
                  {typeof code==='string' ? (
                    <>
                      {(apiOrigin === '' || apiOrigin === "https://master-api.dmhub.cn") && (<Note>Move Code</Note>)}
                      <Code language={language} code={code} path=''/>
                    </>
                  ) : (
                    <Code language={language} code='' path={code.path}/>
                  )}
                </Tabs.TabPane>
              ))}
            </Tabs>
          </Card>
        </Descriptions.Item>
      )}
    </Descriptions>,
  ]
}

export default DataRender
